import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { userSettingsApi } from "@/lib/api";
import { clearUserSnapshot } from "@/lib/userSnapshot";
import { WORKSPACE_TEACHER, otherWorkspace, workspaceHome } from "@/lib/workspaceSwitch";

/**
 * DUAL-ROLE-1-UI — the switcher, as ONE menu item shared by both shells.
 *
 * WHY ONE COMPONENT FOR TWO MENUS. The canon keeps AdminShell's avatar menu and the teacher
 * sidebar's deliberately separate, and this run does not merge them — each keeps its own markup,
 * its own testids and its own roving order. What they share is the DECISION: who is offered a
 * switch, what it does, and what happens when the server refuses. Duplicating that in two files is
 * how the two menus drift until one of them offers a switch the other does not honour.
 *
 * IT RENDERS ONLY ON ELIGIBILITY. `otherWorkspace` returns null unless /api/me says this account may
 * use both workspaces, so a plain admin and a plain teacher render nothing at all — not a disabled
 * item, not a tooltip. Never from a role string, a URL, or a client guess.
 *
 * AND IT NEVER CLAIMS A SWITCH THE SERVER DID NOT RECORD. The PATCH lands first; only its success
 * navigates. A failure stays exactly where it was and says so, because a UI that moves you to a
 * workspace the server still thinks you are not in is worse than one that refuses.
 */
export function WorkspaceSwitchItem({ onDone, className = "", testId }) {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);

  const target = otherWorkspace(user);
  if (!target) return null;

  const label =
    target === WORKSPACE_TEACHER
      ? t("workspaceSwitch.toTeacher")
      : t("workspaceSwitch.toAdmin");

  const onClick = async () => {
    if (pending) return;
    setPending(true);
    // ⚑ W14 (C-352 §1) — CLEAR 4, the workspace switch, and it is the only one of the four that
    // does not pass through `clearStoredAuth`: the token is still perfectly valid, it is the
    // IDENTITY the snapshot describes that is about to change.
    //
    // Cleared BEFORE the PATCH rather than after, so a reload in the middle of a switch cannot
    // paint the workspace the reader is leaving. If the PATCH then fails there is nothing to paint
    // from on the next cold load — one slow first frame, which is the right way round: a stale
    // role in the first frame is a scope defect, a slow frame is only slow.
    clearUserSnapshot();
    try {
      await userSettingsApi.update({ active_workspace: target });
      // The server has recorded it; only now does the session and the address follow.
      // Defensive: the PATCH has already succeeded, so a shell that does not hand us a
      // refreshUser must not turn a recorded switch into a failure the user is told about.
      try { await refreshUser?.(); } catch { /* the settings write stands */ }
      const home = workspaceHome(target);
      if (home) navigate(home, { replace: true });
      if (onDone) onDone();
    } catch (err) {
      // STAY PUT AND SAY SO. No navigation, no optimistic flip of the menu label — the workspace is
      // whatever the server last recorded, and it did not record this.
      toast.error(
        t("workspaceSwitch.failed")
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId || "workspace-switch"}
      onClick={onClick}
      disabled={pending}
      // 44px HIT AREA, logical properties so it flips under RTL with the menu it lives in.
      className={`flex min-h-[44px] w-full items-center justify-start gap-2 rounded-brand px-3 py-2 text-start text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
    >
      {label}
    </button>
  );
}

export default WorkspaceSwitchItem;
